import React, { useState } from 'react';
import axios from 'axios';
import { FileText, PlusCircle, X, Save, AlertCircle, DollarSign, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoicesTab = ({ invoices, cases, timeEntries, refreshData, user }) => {
  const [showCreateInvoiceForm, setShowCreateInvoiceForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    case_id: '',
    client_id: '',
    time_entry_ids: [],
    expenses: [],
    notes: ''
  });
  const [selectedCase, setSelectedCase] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ description: '', amount: '' });

  const handleCaseChange = (e) => {
    const caseId = e.target.value;
    const caseItem = cases.find(c => c.id === caseId);
    setSelectedCase(caseItem);
    setFormData({
      ...formData,
      case_id: caseId,
      client_id: caseItem?.client_id || '',
      time_entry_ids: [],
      expenses: []
    });
  };

  const handleTimeEntryToggle = (entryId) => {
    setFormData(prev => ({
      ...prev,
      time_entry_ids: prev.time_entry_ids.includes(entryId)
        ? prev.time_entry_ids.filter(id => id !== entryId)
        : [...prev.time_entry_ids, entryId]
    }));
  };

  const handleAddExpense = () => {
    if (expenseForm.description && expenseForm.amount) {
      setFormData(prev => ({
        ...prev,
        expenses: [...prev.expenses, {
          description: expenseForm.description,
          amount: parseFloat(expenseForm.amount)
        }]
      }));
      setExpenseForm({ description: '', amount: '' });
    }
  };

  const handleRemoveExpense = (index) => {
    setFormData(prev => ({
      ...prev,
      expenses: prev.expenses.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/solicitor/invoices`, {
        ...formData,
        solicitor_id: user._id
      }, { withCredentials: true });
      
      setFormData({
        case_id: '',
        client_id: '',
        time_entry_ids: [],
        expenses: [],
        notes: ''
      });
      setSelectedCase(null);
      setShowCreateInvoiceForm(false);
      await refreshData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = (invoice) => {
    const invoiceText = `
INVOICE - Ashbourne Law
==========================================

Invoice ID: ${invoice.id}
Solicitor: ${user?.name || 'Unknown'}
Client: ${invoice.client_id || 'Unknown'}
Case: ${invoice.case_id || 'Unknown'}
Amount: £${invoice.amount?.toFixed(2) || '0.00'}
Your Share (70%): £${invoice.split_amount?.toFixed(2) || '0.00'}
Firm Share (30%): £${((invoice.amount - invoice.split_amount) || 0).toFixed(2)}
Status: ${invoice.approval_status}
Created: ${new Date(invoice.created_at).toLocaleDateString('en-GB')}
${invoice.approved_at ? `Approved: ${new Date(invoice.approved_at).toLocaleDateString('en-GB')}` : ''}
${invoice.paid_at ? `Paid: ${new Date(invoice.paid_at).toLocaleDateString('en-GB')}` : ''}

Time Entries:
${invoice.time_entries?.map(entry => `- ${entry.description}: ${entry.hours}h @ £${entry.rate}/h = £${entry.amount?.toFixed(2)}`).join('\n') || 'No time entries'}

Expenses:
${invoice.expenses?.map(exp => `- ${exp.description}: £${exp.amount?.toFixed(2)}`).join('\n') || 'No expenses'}

Notes:
${invoice.notes || 'No notes'}

==========================================
Ashbourne Law - Professional Legal Services
    `;

    const blob = new Blob([invoiceText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice_${invoice.id}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const caseTimeEntries = selectedCase ? timeEntries.filter(e => e.case_id === selectedCase.id && !e.invoiced) : [];
  const selectedEntries = timeEntries.filter(e => formData.time_entry_ids.includes(e.id));
  const subtotal = selectedEntries.reduce((sum, e) => sum + e.amount, 0);
  const expensesTotal = formData.expenses.reduce((sum, e) => sum + e.amount, 0);
  const grossTotal = subtotal + expensesTotal;
  const solicitorShare = grossTotal * 0.70;
  const firmShare = grossTotal * 0.30;

  const statusColors = {
    'draft': 'bg-gray-100 text-gray-800',
    'sent': 'bg-blue-100 text-blue-800',
    'paid': 'bg-green-100 text-green-800',
    'overdue': 'bg-red-100 text-red-800',
    'cancelled': 'bg-gray-100 text-gray-800'
  };

  const approvalStatusColors = {
    'pending_approval': 'bg-orange-100 text-orange-800',
    'approved': 'bg-green-100 text-green-800',
    'rejected': 'bg-red-100 text-red-800'
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-gray-900">Invoices</h2>
          <p className="text-gray-600 mt-1">{invoices.length} total invoices</p>
        </div>
        <button
          onClick={() => setShowCreateInvoiceForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <PlusCircle className="h-5 w-5" />
          <span>Create Invoice</span>
        </button>
      </div>

      {showCreateInvoiceForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-serif text-gray-900">Create Invoice</h3>
              <button onClick={() => setShowCreateInvoiceForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Case *</label>
                <select
                  required
                  value={formData.case_id}
                  onChange={handleCaseChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                >
                  <option value="">Select case...</option>
                  {cases.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>

              {selectedCase && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Select Time Entries</h4>
                    {caseTimeEntries.length > 0 ? (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {caseTimeEntries.map(entry => (
                          <label key={entry.id} className="flex items-center justify-between p-2 hover:bg-white rounded cursor-pointer">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={formData.time_entry_ids.includes(entry.id)}
                                onChange={() => handleTimeEntryToggle(entry.id)}
                                className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 rounded"
                              />
                              <div>
                                <p className="text-sm text-gray-900">{entry.description}</p>
                                <p className="text-xs text-gray-500">{new Date(entry.date).toLocaleDateString()} • {entry.hours}h</p>
                              </div>
                            </div>
                            <span className="text-sm font-medium text-gray-900">£{entry.amount}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No unbilled time entries for this case</p>
                    )}
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Expenses</h4>
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <input
                        type="text"
                        placeholder="Description"
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                        className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <div className="flex space-x-2">
                        <input
                          type="number"
                          step="0.01"
                          placeholder="Amount"
                          value={expenseForm.amount}
                          onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        />
                        <button
                          type="button"
                          onClick={handleAddExpense}
                          className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                    {formData.expenses.length > 0 && (
                      <div className="space-y-1">
                        {formData.expenses.map((expense, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-white rounded">
                            <span className="text-sm text-gray-900">{expense.description}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">£{expense.amount}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveExpense(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-medium text-gray-900 mb-3">Invoice Summary</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Time Entries Subtotal:</span>
                        <span className="font-medium">£{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expenses:</span>
                        <span className="font-medium">£{expensesTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-blue-200">
                        <span className="font-medium text-gray-900">Gross Total:</span>
                        <span className="font-bold text-lg">£{grossTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-blue-200">
                        <span className="text-blue-600 font-medium">Your Share (70%):</span>
                        <span className="text-blue-600 font-bold text-lg">£{solicitorShare.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Firm Share (30%):</span>
                        <span className="text-gray-600 font-medium">£{firmShare.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows="2"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                      placeholder="Additional notes..."
                    />
                  </div>
                </>
              )}

              <div className="flex items-center justify-end space-x-3 pt-4 border-t">
                <button type="button" onClick={() => setShowCreateInvoiceForm(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
                <button type="submit" disabled={loading || !selectedCase || formData.time_entry_ids.length === 0} className="flex items-center space-x-2 px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      <span>Create Invoice</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {invoices.length > 0 ? (
          invoices.map((invoice) => (
              <div key={invoice.id} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-medium text-gray-900">{invoice.invoice_number}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${statusColors[invoice.status]}`}>
                      {invoice.status}
                    </span>
                    {invoice.approval_status && (
                      <span className={`px-2 py-1 text-xs rounded-full font-medium ${approvalStatusColors[invoice.approval_status]}`}>
                        {invoice.approval_status === 'pending_approval' ? '⏳ Pending Approval' : 
                         invoice.approval_status === 'approved' ? '✓ Approved' : 
                         '✗ Rejected'}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">Case ID: {invoice.case_id}</p>
                  {invoice.approval_status === 'rejected' && invoice.rejection_reason && (
                    <div className="mb-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-800">
                        <strong>Rejection Reason:</strong> {invoice.rejection_reason}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Gross Total</p>
                      <p className="font-medium text-gray-900">£{invoice.gross_total.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Your 70%</p>
                      <p className="font-medium text-blue-600">£{invoice.solicitor_share.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Firm 30%</p>
                      <p className="font-medium text-gray-600">£{invoice.firm_share.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Issue Date</p>
                      <p className="font-medium text-gray-900">{new Date(invoice.issue_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-500">
                  Due: {new Date(invoice.due_date).toLocaleDateString()}
                </div>
                <div className="flex items-center space-x-3">
                  <button 
                    onClick={() => handleDownloadInvoice(invoice)}
                    className="text-sm text-gray-900 hover:underline font-medium flex items-center space-x-1"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download</span>
                  </button>
                  <button className="text-sm text-gray-900 hover:underline font-medium">
                    View Details →
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices yet</h3>
            <p className="text-gray-600 mb-4">Create your first invoice from tracked time</p>
            <button onClick={() => setShowCreateInvoiceForm(true)} className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors">
              <PlusCircle className="h-5 w-5" />
              <span>Create Invoice</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicesTab;